import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, Phone } from "lucide-react";
import { ORDER_URL, RESERVE_URL, PHONE, PHONE_LINK, getOpenState } from "@/data/menu";
import { StatusBadge } from "@/components/StatusBadge";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const COLLAGE = [
  {
    src: "https://images.unsplash.com/photo-1756821753095-64134f5c0c5c?fm=jpg&q=80&w=1200&auto=format&fit=crop",
    alt: "Dal makhani — slow-cooked black lentils",
    frame: "mt-10 h-48 rounded-[1.5rem] sm:h-64",
  },
  {
    src: "https://images.pexels.com/photos/35158690/pexels-photo-35158690.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Butter chicken curry in a brass bowl",
    frame: "h-60 rounded-t-[999px] rounded-b-[1.5rem] sm:h-80",
  },
  {
    src: "https://images.pexels.com/photos/28674541/pexels-photo-28674541.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Paneer tikka masala in rich tomato gravy",
    frame: "mt-16 h-48 rounded-[1.5rem] sm:h-60",
  },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yMid = useTransform(scrollY, [0, 700], [0, 50]);
  const ySide = useTransform(scrollY, [0, 700], [0, -40]);
  const state = getOpenState();

  return (
    <section id="top" className="relative overflow-hidden bg-[#2B170D]" data-testid="hero-section">
      <div aria-hidden className="pointer-events-none absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-[#E32219]/25 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -right-24 top-40 h-80 w-80 rounded-full bg-[#FFC61A]/15 blur-[110px]" />

      <div className="relative mx-auto max-w-5xl px-5 pt-32 text-center sm:px-8 sm:pt-36">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.4 }}
          className="mt-7 flex items-center justify-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.3em] text-[#FFC61A]"
          data-testid="hero-eyebrow"
        >
          <MapPin size={13} strokeWidth={1.75} />
          Toombul Rd · Northgate · Brisbane
        </motion.p>

        <h1 className="mt-5 font-display text-5xl font-light leading-[1.04] tracking-tight text-[#FFF9F1] sm:text-7xl" data-testid="hero-title">
          <Line delay={0.5}>Flavour, fired fresh</Line>
          <Line delay={0.63}>
            <span className="italic text-[#FFC61A]">the Indian way</span> —
          </Line>
          <Line delay={0.76}>in Northgate.</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1 }}
          className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#FFF9F1]/70 sm:text-lg"
          data-testid="hero-subtitle"
        >
          Slow-simmered curries, tawa biryanis and naan straight from the oven.
          Order ahead or book a table — dinner is sorted.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-5 font-mono-alt text-[11px] uppercase tracking-[0.4em] text-[#FFC61A]"
          data-testid="hero-cuisine"
        >
          Authentic Indian Cuisine
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.2 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-[#FFF9F1] transition-all duration-300 hover:bg-[#B8170F] hover:shadow-[0_16px_36px_-12px_rgba(227,34,25,0.8)]"
          >
            Order Online
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={RESERVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-reserve-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#FFF9F1]/35 px-8 py-4 text-sm font-medium text-[#FFF9F1] transition-all duration-300 hover:bg-[#FFF9F1] hover:text-[#2B170D]"
          >
            Reserve a Table
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.35 }}
          className="mt-6 flex justify-center"
        >
          <StatusBadge state={state} testid="hero-status-badge" />
        </motion.div>
      </div>

      <div className="relative mx-auto mb-20 mt-16 grid max-w-5xl grid-cols-3 items-start gap-4 px-5 sm:gap-6 sm:px-8">
        {COLLAGE.map((img, i) => (
          <motion.div
            key={img.alt}
            style={{ y: i === 1 ? yMid : ySide }}
            initial={{ opacity: 0, y: 44 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.1 + i * 0.12 }}
            className="overflow-hidden"
          >
            <img
              src={img.src}
              alt={img.alt}
              data-testid={i === 1 ? "hero-image" : `hero-collage-${i}`}
              className={`w-full object-cover ${img.frame}`}
              loading="eager"
            />
          </motion.div>
        ))}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.5 }}
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-2xl border border-[#3A1F14]/10 bg-[#FFF9F1]/90 px-5 py-4 shadow-[0_20px_44px_-18px_rgba(0,0,0,0.55)] backdrop-blur-xl"
          data-testid="hero-dish-card"
        >
          <p className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-sage-dark">House favourite</p>
          <p className="mt-1 font-display text-xl font-semibold text-forest">Butter Chicken <span className="text-sm font-semibold text-terracotta">$19.24</span></p>
        </motion.div>
      </div>

      <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t border-[#FFF9F1]/15 py-7"
          data-testid="hero-facts"
        >
          <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#FFF9F1]/60">
            Dine-in · Takeaway · Delivery
          </span>
          <a
            href={PHONE_LINK}
            data-testid="hero-phone-link"
            className="flex items-center gap-2 font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#FFF9F1]/60 transition-colors hover:text-[#FFC61A]"
          >
            <Phone size={12} strokeWidth={1.75} /> {PHONE}
          </a>
          <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#FFF9F1]/60">
            Closed Sundays
          </span>
        </motion.div>
      </div>
    </section>
  );
};
